import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateMerchantDraftInput,
  MerchantDraftItem,
  UpdateMerchantDraftInput,
  WorkspaceMerchantItem,
} from "@unbox/api";
import { handleError } from "@/api/error";
import { useWorkspaceId } from "@/providers/MerchantProvider";
import { downloadLogo } from "@/utils/logo-dev";
import { client } from "./client";

export type { MerchantDraftItem, UpdateMerchantDraftInput, WorkspaceMerchantItem };

const merchantsKey = (workspaceId: string | null) => ["workspace-merchants", workspaceId];
const draftKey = (workspaceId: string | null) => ["merchant-draft", workspaceId];

const useRequiredWorkspaceId = () => {
  const workspaceId = useWorkspaceId();
  return () => {
    if (!workspaceId) throw new Error("No workspace selected");
    return workspaceId;
  };
};

export const useWorkspaceMerchants = () => {
  const workspaceId = useWorkspaceId();

  return useQuery<WorkspaceMerchantItem[]>({
    queryKey: merchantsKey(workspaceId),
    queryFn: async () => {
      if (!workspaceId) throw new Error("No workspace selected");
      const res = await client.workspace[":workspaceId"].merchants.$get({ param: { workspaceId } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!workspaceId,
  });
};

export const useMerchantDraft = ({ pollUntilConnected = false }: { pollUntilConnected?: boolean } = {}) => {
  const workspaceId = useWorkspaceId();

  return useQuery<MerchantDraftItem | null>({
    refetchInterval: (query) => {
      const draft = query.state.data;
      if (!pollUntilConnected || !draft) return false;
      return draft.shopConnectedAt ? false : 4000;
    },
    queryKey: draftKey(workspaceId),
    queryFn: async () => {
      if (!workspaceId) throw new Error("No workspace selected");
      const res = await client.workspace[":workspaceId"]["merchant-draft"].$get({ param: { workspaceId } });
      if (!res.ok) throw await handleError(res);
      return (await res.json()).draft;
    },
    enabled: !!workspaceId,
  });
};

export const useRefreshMerchantDraft = () => {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: draftKey(workspaceId) });
};

export const useCreateMerchantDraft = () => {
  const requireWorkspaceId = useRequiredWorkspaceId();
  const invalidateDraft = useRefreshMerchantDraft();

  return useMutation({
    mutationFn: async (json: CreateMerchantDraftInput) => {
      const res = await client.workspace[":workspaceId"]["merchant-draft"].$post({
        param: { workspaceId: requireWorkspaceId() },
        json,
      });
      if (!res.ok) throw await handleError(res);
      return (await res.json()).draft;
    },
    onSuccess: invalidateDraft,
  });
};

export const useUpdateMerchantDraft = () => {
  const requireWorkspaceId = useRequiredWorkspaceId();
  const invalidateDraft = useRefreshMerchantDraft();

  return useMutation({
    mutationFn: async (json: UpdateMerchantDraftInput) => {
      const res = await client.workspace[":workspaceId"]["merchant-draft"].$patch({
        param: { workspaceId: requireWorkspaceId() },
        json,
      });
      if (!res.ok) throw await handleError(res);
      return (await res.json()).draft;
    },
    onSuccess: invalidateDraft,
  });
};

export const useDiscardMerchantDraft = () => {
  const requireWorkspaceId = useRequiredWorkspaceId();
  const invalidateDraft = useRefreshMerchantDraft();

  return useMutation({
    mutationFn: async () => {
      const res = await client.workspace[":workspaceId"]["merchant-draft"].$delete({
        param: { workspaceId: requireWorkspaceId() },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: invalidateDraft,
  });
};

export const useLogoSearch = (query: string) => {
  const workspaceId = useWorkspaceId();
  const q = query.trim();

  return useQuery({
    queryKey: ["merchant-draft-logo-search", workspaceId, q],
    queryFn: async () => {
      if (!workspaceId) throw new Error("No workspace selected");
      const res = await client.workspace[":workspaceId"]["merchant-draft"]["logo-search"].$get({
        param: { workspaceId },
        query: { q },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!workspaceId && q.length >= 2,
    staleTime: 5 * 60 * 1000,
  });
};

export const useLogoLookup = (domain: string) => {
  const workspaceId = useWorkspaceId();
  const value = domain.trim();

  return useQuery({
    queryKey: ["merchant-draft-logo-lookup", workspaceId, value],
    queryFn: async () => {
      if (!workspaceId) throw new Error("No workspace selected");
      const res = await client.workspace[":workspaceId"]["merchant-draft"]["logo-lookup"].$get({
        param: { workspaceId },
        query: { domain: value },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!workspaceId && value.includes("."),
    staleTime: 5 * 60 * 1000,
  });
};

export const useKeepFoundLogo = () => {
  const requireWorkspaceId = useRequiredWorkspaceId();
  const invalidateDraft = useRefreshMerchantDraft();

  return useMutation({
    mutationFn: async (domain: string) => {
      const logo = await downloadLogo(domain);
      const res = await client.workspace[":workspaceId"]["merchant-draft"].logo.$post({
        param: { workspaceId: requireWorkspaceId() },
        form: { logo, domain },
      });
      if (!res.ok) throw await handleError(res);
      return (await res.json()).draft;
    },
    onSuccess: invalidateDraft,
  });
};

export const useUploadDraftLogo = () => {
  const requireWorkspaceId = useRequiredWorkspaceId();
  const invalidateDraft = useRefreshMerchantDraft();

  return useMutation({
    mutationFn: async (logo: File) => {
      const res = await client.workspace[":workspaceId"]["merchant-draft"].logo.$post({
        param: { workspaceId: requireWorkspaceId() },
        form: { logo },
      });
      if (!res.ok) throw await handleError(res);
      return (await res.json()).draft;
    },
    onSuccess: invalidateDraft,
  });
};

export const useRemoveDraftLogo = () => {
  const requireWorkspaceId = useRequiredWorkspaceId();
  const invalidateDraft = useRefreshMerchantDraft();

  return useMutation({
    mutationFn: async () => {
      const res = await client.workspace[":workspaceId"]["merchant-draft"].logo.$delete({
        param: { workspaceId: requireWorkspaceId() },
      });
      if (!res.ok) throw await handleError(res);
      return (await res.json()).draft;
    },
    onSuccess: invalidateDraft,
  });
};

export const useChooseDraftShop = () => {
  const requireWorkspaceId = useRequiredWorkspaceId();
  const invalidateDraft = useRefreshMerchantDraft();

  return useMutation({
    mutationFn: async (shopId: string) => {
      const res = await client.workspace[":workspaceId"]["merchant-draft"].tts.shop.$post({
        param: { workspaceId: requireWorkspaceId() },
        json: { shopId },
      });
      if (!res.ok) throw await handleError(res);
      return (await res.json()).draft;
    },
    onSuccess: invalidateDraft,
  });
};

export const useDraftAuthUrls = () => {
  const requireWorkspaceId = useRequiredWorkspaceId();

  return useMutation({
    mutationFn: async () => {
      const res = await client.workspace[":workspaceId"]["merchant-draft"].tts["auth-url"].$get({
        param: { workspaceId: requireWorkspaceId() },
      });
      if (!res.ok) throw await handleError(res);
      return (await res.json()).authUrl;
    },
  });
};

export const useSubmitMerchantDraft = () => {
  const requireWorkspaceId = useRequiredWorkspaceId();
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (version: number) => {
      const res = await client.workspace[":workspaceId"]["merchant-draft"].submit.$post({
        param: { workspaceId: requireWorkspaceId() },
        json: { version },
      });
      if (!res.ok) throw await handleError(res);
      return (await res.json()).merchant;
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: merchantsKey(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: ["me"] }),
      ]),
  });
};

export const useSaveMerchantDraftForApproval = () => {
  const requireWorkspaceId = useRequiredWorkspaceId();
  const invalidateDraft = useRefreshMerchantDraft();

  return useMutation({
    mutationFn: async (version: number) => {
      const res = await client.workspace[":workspaceId"]["merchant-draft"]["save-for-approval"].$post({
        param: { workspaceId: requireWorkspaceId() },
        json: { version },
      });
      if (!res.ok) throw await handleError(res);
      return (await res.json()).draft;
    },
    onSuccess: invalidateDraft,
  });
};
